import { useState, useRef, useEffect } from 'react'
import { colors } from '../styles/tokens'
import { MODULE_REGISTRY } from '../lib/quantityEngine'

// =======================================================================
// ModulePickerPopover — the ONE scalable module selector for ANY number
// of dedicated engineering modules. It reads MODULE_REGISTRY dynamically
// and groups every module with `isDedicatedPanel: true` by its `family`
// field — adding a future module (Masonry, MEP, Formwork, ...) requires
// ONLY registering it with a `family` and `label`; this component never
// needs to change.
//
// Modules without a `family` fall into a generic "Other" group rather
// than being silently dropped, so a registration mistake is visible
// instead of hidden.
// =======================================================================

const FAMILY_LABELS = {
  rebar: 'REBAR',
  finishes: 'FINISHES',
  concrete_earthwork: 'CONCRETE & EARTHWORK',
}

export default function ModulePickerPopover({ selectedKey, onSelect }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const dedicatedModules = Object.values(MODULE_REGISTRY).filter((m) => m.isDedicatedPanel)
  const groups = {}
  for (const mod of dedicatedModules) {
    const family = mod.family || 'other'
    if (!groups[family]) groups[family] = []
    groups[family].push(mod)
  }

  const selectedModule = selectedKey ? MODULE_REGISTRY[selectedKey] : null

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: '8px 14px', borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff',
          cursor: 'pointer', fontSize: 12, fontWeight: 700, color: selectedModule ? colors.primary : colors.textSecondary,
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {selectedModule ? selectedModule.label : '+ Choose Engineering Module'}
        <span style={{ fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20, minWidth: 240,
          background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 320, overflowY: 'auto', padding: 6,
        }}>
          {Object.entries(groups).map(([family, mods]) => (
            <div key={family} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, padding: '6px 10px 2px', letterSpacing: 0.4 }}>
                {FAMILY_LABELS[family] || family.toUpperCase()}
              </div>
              {mods.map((mod) => (
                <button
                  key={mod.moduleKey}
                  onClick={() => { onSelect(mod.moduleKey); setOpen(false) }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 5,
                    border: 'none', cursor: 'pointer', fontSize: 12,
                    background: selectedKey === mod.moduleKey ? '#F0F4FF' : 'transparent',
                    color: selectedKey === mod.moduleKey ? colors.primary : colors.textPrimary,
                    fontWeight: selectedKey === mod.moduleKey ? 700 : 400,
                  }}
                  onMouseEnter={(e) => { if (selectedKey !== mod.moduleKey) e.currentTarget.style.background = colors.bg }}
                  onMouseLeave={(e) => { if (selectedKey !== mod.moduleKey) e.currentTarget.style.background = 'transparent' }}
                >
                  {mod.label.replace(/^.*—\s*/, '• ')}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
